import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, ArrowRight } from 'lucide-react';
import { Card, CardContent, useToast } from '@mustauto/ui/web';
import type { WorkshopProfile } from '@/hooks/useSettings';
import { formatApiError } from '@mustauto/utils';

interface CompletionStepProps {
  profile: WorkshopProfile | null;
  onComplete: () => Promise<void> | void;
}

export function CompletionStep({ profile, onComplete }: CompletionStepProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const settings = profile?.settings;
  const checks = [
    { label: 'Dados da oficina', done: !!profile?.name },
    { label: 'Endereço', done: !!settings?.address },
    { label: 'Especialidades', done: (settings?.specialties?.length ?? 0) > 0 },
    { label: 'Horários', done: (settings?.hours?.length ?? 0) > 0 },
    { label: 'Fotos', done: (settings?.photos?.length ?? 0) > 0 },
  ];

  const handleAction = async (destination: '/service-orders' | '/dashboard') => {
    if (loading) return;
    setLoading(true);
    try {
      // Sem seed de demonstração: a oficina do cliente nasce vazia, com o dado
      // real dele. Quem quer conhecer o produto antes de cadastrar abre a
      // oficina-demo compartilhada pelo botão do login.
      await onComplete();
      navigate({ to: destination });
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível concluir',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
      {/* Confetti CSS animation */}
      <div className="confetti-container" aria-hidden="true">
        {Array.from({ length: 30 }).map((_, i) => (
          <div
            key={i}
            className="confetti-piece"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
              // Confete de conclusão do onboarding: aqui a turquesa entra por
              // ser celebração de marca, não estado de alguma coisa.
              backgroundColor: [
                'var(--ma-teal-500)',
                'var(--ma-teal-300)',
                'var(--ma-navy-800)',
                'var(--ma-success)',
                'var(--ma-warning)',
              ][i % 5],
            }}
          />
        ))}
      </div>

      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/40">
        <Check size={32} className="text-emerald-700 dark:text-emerald-300" />
      </div>

      <h1 className="text-2xl font-bold">Sua oficina está pronta!</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Tudo configurado. Agora você pode começar a usar o MustAuto.
      </p>

      {/* Summary card */}
      <Card className="mt-8 w-full max-w-sm text-left">
        <CardContent className="p-4 space-y-2">
          {checks.map((c) => (
            <div key={c.label} className="flex items-center gap-2">
              <div
                className={`flex h-5 w-5 items-center justify-center rounded-full ${
                  c.done ? 'bg-emerald-100 dark:bg-emerald-950/40' : 'bg-muted'
                }`}
              >
                {c.done ? (
                  <Check size={12} className="text-emerald-700 dark:text-emerald-300" />
                ) : (
                  <span className="text-[10px] text-muted-foreground">-</span>
                )}
              </div>
              <span
                className={`text-sm ${c.done ? 'text-foreground' : 'text-muted-foreground'}`}
              >
                {c.label}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="mt-8 flex flex-col gap-3 w-full max-w-sm">
        <button
          onClick={() => handleAction('/service-orders')}
          disabled={loading}
          className="flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {loading ? 'Carregando...' : 'Abrir primeira OS'}
          {!loading && <ArrowRight size={16} />}
        </button>
        <button
          onClick={() => handleAction('/dashboard')}
          disabled={loading}
          className="rounded-lg border border-border px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        >
          Ir para o dashboard
        </button>
        <a
          href="https://mustauto.com.br/guia"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground underline hover:text-foreground transition-colors"
        >
          Ver o guia de primeiros passos
        </a>
      </div>

      {/* Inline confetti styles */}
      <style>{`
        .confetti-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
          z-index: 60;
        }
        .confetti-piece {
          position: absolute;
          top: -10px;
          width: 8px;
          height: 8px;
          border-radius: 2px;
          animation: confetti-fall linear forwards;
          opacity: 0.9;
        }
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
